import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';
import type { User, Tenant } from '../../../shared/backend/types';
import { apiClient } from '../lib/api-client';

const AUTH_TOKEN_KEY = 'auth_token';

function getAuthTokenFromStorage(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

function setAuthTokenInStorage(token: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

function deleteAuthTokenFromStorage(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

const initialState: AuthState = {
  user: null,
  tenant: null,
  isLoading: true,
  isAuthenticated: false,
  error: null,
};

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; tenant: Tenant | null } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'AUTH_CLEAR_ERROR' }
  | { type: 'AUTH_UPDATE_USER'; payload: Partial<User> };

interface AuthContextValue {
  state: AuthState;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
}

interface LoginResponse {
  success: boolean;
  data?: {
    user: User;
    tenant: Tenant | null;
    token?: string;
  };
  error?: {
    message: string;
  };
}

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, isLoading: true, error: null };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: action.payload.user,
        tenant: action.payload.tenant,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        tenant: null,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return { ...initialState, isLoading: false };
    case 'AUTH_CLEAR_ERROR':
      return { ...state, error: null };
    case 'AUTH_UPDATE_USER':
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null,
      };
    default:
      return state;
  }
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): React.ReactElement {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const initializeAuth = useCallback(async () => {
    const token = getAuthTokenFromStorage();
    if (!token) {
      dispatch({ type: 'AUTH_LOGOUT' });
      return;
    }

    dispatch({ type: 'AUTH_START' });

    try {
      const response = await apiClient.getProfile();
      if (response.success && response.data) {
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user: response.data, tenant: null },
        });
      } else {
        deleteAuthTokenFromStorage();
        dispatch({ type: 'AUTH_LOGOUT' });
      }
    } catch {
      deleteAuthTokenFromStorage();
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    dispatch({ type: 'AUTH_START' });

    const baseUrl = import.meta.env.PUBLIC_API_BASE_URL || 'https://casino-saas-api.eu-nl.workers.dev';

    try {
      const response = await fetch(`${baseUrl}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json() as LoginResponse;

      if (!response.ok || !data.success) {
        throw new Error(data.error?.message || 'Login failed');
      }

      if (data.data?.token) {
        setAuthTokenInStorage(data.data.token);
      }

      if (data.data) {
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user: data.data.user,
            tenant: data.data.tenant,
          },
        });
      }
    } catch (error) {
      dispatch({
        type: 'AUTH_FAILURE',
        payload: error instanceof Error ? error.message : 'Login failed',
      });
    }
  }, []);

  const logout = useCallback(async () => {
    const token = getAuthTokenFromStorage();
    const baseUrl = import.meta.env.PUBLIC_API_BASE_URL || 'https://casino-saas-api.eu-nl.workers.dev';

    if (token) {
      try {
        await fetch(`${baseUrl}/api/v1/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {}
    }

    deleteAuthTokenFromStorage();
    dispatch({ type: 'AUTH_LOGOUT' });
  }, []);

  const refreshUser = useCallback(async () => {
    if (!state.isAuthenticated) return;

    try {
      const response = await apiClient.getProfile();
      if (response.success && response.data) {
        dispatch({ type: 'AUTH_UPDATE_USER', payload: response.data });
      }
    } catch {}
  }, [state.isAuthenticated]);

  const clearError = useCallback(() => {
    dispatch({ type: 'AUTH_CLEAR_ERROR' });
  }, []);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const value = useMemo<AuthContextValue>(
    () => ({ state, login, logout, refreshUser, clearError }),
    [state, login, logout, refreshUser, clearError]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function useUser(): User | null {
  const { state } = useAuth();
  return state.user;
}

export function useIsAuthenticated(): boolean {
  const { state } = useAuth();
  return state.isAuthenticated;
}

export function useIsAuthLoading(): boolean {
  const { state } = useAuth();
  return state.isLoading;
}
